import { Card } from "@/types/v2";

export interface SortCriteria {
  field: keyof Card | string;
  direction: "asc" | "desc";
  priority: number; // For multi-level sorting (0 = highest priority)
}

export interface SortingState {
  criteria: SortCriteria[];
  maxLevels: number; // Maximum number of sort levels allowed
}

export interface SortIndicatorProps {
  direction?: "asc" | "desc";
  priority?: number;
  isActive: boolean;
}

export interface MultiLevelSortConfig {
  enabled: boolean;
  maxLevels: number;
  allowDragReorder: boolean;
  showPriorityIndicators: boolean;
  persistUserPreferences: boolean;
}

export interface ColumnConfig {
  id: string;
  field: keyof Card | string;
  label: string;
  width: number;
  minWidth: number;
  maxWidth?: number;
  sortable: boolean;
  filterable: boolean;
  visible: boolean;
  fixed: boolean; // Cannot be hidden
  align: "left" | "center" | "right";
  formatter?: (value: unknown, card: Card) => React.ReactNode;
  className?: string;
  labelResolver?: () => string; // Function to resolve dynamic label
  fallbackLabel?: string; // Static fallback label for dynamic columns
}

export const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    id: "primary",
    field: "primaryField",
    label: "Primary",
    width: 200,
    minWidth: 150,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: true,
    align: "left",
  },
  {
    id: "secondary",
    field: "secondaryField",
    label: "Secondary",
    width: 150,
    minWidth: 120,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "tertiary",
    field: "tertiaryField",
    label: "Tertiary",
    width: 150,
    minWidth: 120,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "technician",
    field: "technicianId",
    label: "Technician",
    width: 120,
    minWidth: 100,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "serviceAdvisor",
    field: "serviceWriterId",
    label: "Service Advisor",
    width: 140,
    minWidth: 120,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "container",
    field: "currentContainer",
    label: "Container",
    width: 120,
    minWidth: 100,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "timer",
    field: "timer",
    label: "Timer",
    width: 100,
    minWidth: 80,
    sortable: true,
    filterable: false,
    visible: true,
    fixed: false,
    align: "center",
  },
  {
    id: "tags",
    field: "tags",
    label: "Tags",
    width: 150,
    minWidth: 120,
    sortable: false,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "promiseTime",
    field: "promisedTime",
    label: "Promise Time",
    width: 130,
    minWidth: 110,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "left",
  },
  {
    id: "laborHours",
    field: "totalLaborHours",
    label: "Labor Hours",
    width: 110,
    minWidth: 90,
    sortable: true,
    filterable: true,
    visible: true,
    fixed: false,
    align: "right",
  },
];

export const DEFAULT_MULTI_LEVEL_SORT_CONFIG: MultiLevelSortConfig = {
  enabled: true,
  maxLevels: 3,
  allowDragReorder: true,
  showPriorityIndicators: true,
  persistUserPreferences: true,
};

export const DEFAULT_SORTING_STATE: SortingState = {
  criteria: [],
  maxLevels: DEFAULT_MULTI_LEVEL_SORT_CONFIG.maxLevels,
};

// Grouping Types
export type GroupByOption =
  | "none"
  | "container"
  | "technician"
  | "serviceAdvisor";

export interface GroupingConfig {
  groupBy: GroupByOption;
  showStatistics: boolean;
  showCounts: boolean;
  collapsedGroups: string[];
}

export interface GroupStatistics {
  cardCount: number;
  totalLaborHours: number;
  averageLaborHours: number;
  promiseTimeViolations: number;
}

export interface CardGroup {
  id: string;
  label: string;
  cards: Card[];
  statistics: GroupStatistics;
  isCollapsed: boolean;
}

export interface GroupingState {
  config: GroupingConfig;
  groups: CardGroup[];
}

export const DEFAULT_GROUPING_CONFIG: GroupingConfig = {
  groupBy: "none",
  showStatistics: true,
  showCounts: true,
  collapsedGroups: [],
};
